import moment from "moment";

import { Client } from "@/Common/Api/Client";
import { EndpointInfoClient, IEndpointInfo } from "@/Common/Api/EndpointInfoClient";
import { ExternalServiceClient, QueryParams } from "@/Common/Api/ExternalServiceClient";
import * as ServerOperations from "@/Common/Operations/operations.server";
import {
    DirectoryAddressSearchDto,
    DirectoryEntityType,
    DirectoryFaxNumberSearchDto,
    DirectoryHealthCareProviderSearchDto,
    DirectoryOtherEstablishmentSearchDto,
    DirectoryPharmacySearchDto,
    SearchOrder
} from "@/ContactDirectory/Api/medfar-contactdirectory/dist";
import { DirectoryHealthCareProvider } from "../Models/DirectoryHealthCareProvider";
import { DirectoryOtherEstablishment } from "../Models/DirectoryOtherEstablishment";
import { DirectoryPharmacy } from "../Models/DirectoryPharmacy";
import { DirectorySelectableEntity } from "../Models/DirectorySelectableEntity";
import { DuplicateSearchType } from "../Models/enums/DuplicateSearchType";
import { IDirectoryEntity } from "../Models/IDirectoryEntity";
import {
    DirectoryEntityAddDto,
    DirectoryEntityExistsParameters,
    DirectoryEntityModifyDto,
    DirectoryEntitySearchDto,
    DirectoryEntitySearchDuplicateDto
} from "./ClientParameters/DirectoryEntityDto";
import { DirectoryHealthCareProviderExistsParameters } from "./ClientParameters/DirectoryHealthCareProviderExistsParameters";
import { DirectoryHealthCareProviderSearchDuplicateDto } from "./ClientParameters/DirectoryHealthCareProviderSearchDuplicateDto";
import { DirectoryOtherEstablishmentExistsParameters } from "./ClientParameters/DirectoryOtherEstablishmentExistsParameters";
import { DirectoryPharmacyAddressExistsParameters } from "./ClientParameters/DirectoryPharmacyAddressExistsParameters";
import { DirectoryPharmacyFaxNumberExistsParameters, PharmacyExistsParameterType } from "./ClientParameters/DirectoryPharmacyFaxNumberExistsParameters";
import { DirectoryEntityMapper } from "./Mapper/DirectoryEntityMapper";
import { PharmacySearchDto } from "./medfar-contactdirectory/dist/models/PharmacySearchDto";

interface SearchResponse {
    pharmacies: DirectoryPharmacySearchDto[];
    healthCareProviders: DirectoryHealthCareProviderSearchDto[];
    otherEstablishments: DirectoryOtherEstablishmentSearchDto[];
    searchOrder?: SearchOrder[];
}

type PharmacyExistsTypeDto = DirectoryPharmacyAddressExistsParameters | DirectoryPharmacyFaxNumberExistsParameters;

/**
 * An API client which fetches and caches access info for the contact directory service.
 */
class ContactDirectoryEndpointInfoClient extends EndpointInfoClient {
    public constructor(apiVersion: string) {
        super(ServerOperations.Server.Myle_LoadBearerTokenForContactDirectory, apiVersion);
    }

    protected async getAccessInfoFromServer(): Promise<IEndpointInfo> {
        try {
            const response = await Client.postAsync<{ BaseUri: string, Token: string, Ttl: number }>(this.operation, { apiVersion: this.apiVersion });
            const data = response.data;

            return {
                baseUri: data.BaseUri,
                token: data.Token,
                ttl: data.Ttl ?? EndpointInfoClient.defaultTimeToLiveInMin,
                tokenGeneratedDateTime: moment().toDate()
            };
        }
        catch (error) {
            console.error(error);
        }
    }
}

/**
 * Client to access the contact directory service
 */
class ContactDirectoryClient {
    public readonly apiVersion = process.env.REACT_APP_CONTACT_DIRECTORY_API_VERSION;

    private static instance: ContactDirectoryClient;

    private readonly routes = {
        [DirectoryEntityType.Other]: "OtherEstablishment",
        [DirectoryEntityType.HealthcareProvider]: "HealthcareProvider",
        [DirectoryEntityType.Pharmacy]: "Pharmacy"
    };

    private externalServiceClient: ExternalServiceClient;

    private constructor() {
        const endpointInfoClient = new ContactDirectoryEndpointInfoClient(this.apiVersion);
        this.externalServiceClient = new ExternalServiceClient(endpointInfoClient);
    }

    public static getInstance(): ContactDirectoryClient {
        if (!ContactDirectoryClient.instance)
            ContactDirectoryClient.instance = new ContactDirectoryClient();

        return ContactDirectoryClient.instance;
    }

    public clearCache(): void {
        this.externalServiceClient.clearCache();
    }

    public search(searchText: string, types?: DirectoryEntityType[], countryId?: string, stateId?: string, exclusiveGeographicSearch: boolean = true): Promise<SearchResponse> {
        const queryParams: QueryParams = { searchText };

        if (types)
            queryParams["searchTypes"] = types.map(p => `${p}`);

        if (!IsNullOrWhiteSpace(countryId))
            queryParams["countryId"] = countryId;

        if (!IsNullOrWhiteSpace(stateId))
            queryParams["stateId"] = stateId;

        queryParams["exclusiveGeographicSearch"] = exclusiveGeographicSearch;

        return this.externalServiceClient.getAsync<SearchResponse>("search", null, queryParams, true);
    }

    public create(directoryType: DirectoryEntityType, model: DirectoryEntityAddDto): Promise<DirectoryEntitySearchDto> {
        return this.externalServiceClient.postAsync(`${this.routes[directoryType]}/add`, model);
    }

    public flagAsInvalid(directoryType: DirectoryEntityType, id: string, reason: string): Promise<void> {
        return this.externalServiceClient.putAsync(`${this.routes[directoryType]}/flagAsInvalid`, { id, reason });
    }

    public modify(directoryType: DirectoryEntityType, model: DirectoryEntityModifyDto): Promise<DirectoryEntitySearchDto> {
        return this.externalServiceClient.putAsync(`${this.routes[directoryType]}/modify`, model);
    }

    public exists(directoryType: DirectoryEntityType, dto: DirectoryEntityExistsParameters): Promise<boolean> {
        const route = `${this.routes[directoryType]}/exists`;
        let pathParams = [];
        const queryParams: QueryParams = {};

        switch (directoryType) {
            case DirectoryEntityType.Pharmacy:
                const pharmacyDto = dto as PharmacyExistsTypeDto;
                if (pharmacyDto.type === PharmacyExistsParameterType.FaxNumber) {
                    const faxNumberExistDto = pharmacyDto as DirectoryPharmacyFaxNumberExistsParameters;
                    pathParams = [faxNumberExistDto.faxNumber, faxNumberExistDto.countryId];
                    if (faxNumberExistDto?.exceptPharmacyId)
                        queryParams["exceptPharmacyId"] = faxNumberExistDto.exceptPharmacyId?.toString();
                }
                else if (pharmacyDto.type === PharmacyExistsParameterType.Address) {
                    const addressExistDto = pharmacyDto as DirectoryPharmacyAddressExistsParameters;
                    pathParams = [addressExistDto.countryId, addressExistDto.stateId, addressExistDto.city, addressExistDto.streetAddress];
                    if (addressExistDto?.exceptPharmacyId)
                        queryParams["exceptPharmacyId"] = addressExistDto.exceptPharmacyId?.toString();
                }
                break;
            case DirectoryEntityType.Other:
                const otherEstablishmentDto = dto as DirectoryOtherEstablishmentExistsParameters;
                pathParams = [otherEstablishmentDto.name];
                if (otherEstablishmentDto?.exceptOtherEstablishmentId)
                    queryParams["exceptOtherEstablishmentId"] = otherEstablishmentDto?.exceptOtherEstablishmentId?.toString();
                break;
            case DirectoryEntityType.HealthcareProvider:
                const healthCareProviderExistsParameters = dto as DirectoryHealthCareProviderExistsParameters;
                pathParams = [healthCareProviderExistsParameters.licenseOrganizationRuleId, healthCareProviderExistsParameters.licenseNumber];
                if (healthCareProviderExistsParameters?.exceptHealthCareProviderId)
                    queryParams["exceptHealthCareProviderId"] = healthCareProviderExistsParameters.exceptHealthCareProviderId?.toString();
                break;
        }

        return this.externalServiceClient.getAsync<boolean>(route, pathParams, queryParams);
    }

    public searchDuplicates(entityId: string | null, dto: DirectoryEntitySearchDuplicateDto, type: DirectoryEntityType, duplicateSearchType: DuplicateSearchType): Promise<SearchResponse> {
        let route = `${this.routes[type]}/searchDuplicates`;
        let pathParams = [];
        let queryParams: QueryParams = {};

        if (!IsNullOrUndefined(entityId))
            queryParams["entityId"] = entityId?.toString();

        switch (duplicateSearchType) {
            case DuplicateSearchType.FaxNumber:
                const faxNumberDto = dto as DirectoryFaxNumberSearchDto;
                pathParams = [faxNumberDto.number, faxNumberDto.countryId];
                route = `${route}/faxNumber`;
                break;
            case DuplicateSearchType.Address:
                const addressDto = dto as DirectoryAddressSearchDto;
                pathParams = [addressDto.countryId, addressDto.stateId, addressDto.city, addressDto.streetAddress];
                route = `${route}/address`;
                break;
            case DuplicateSearchType.Name:
                const name = dto as string;
                pathParams = [name];
                route = `${route}/name`;
                break;
            case DuplicateSearchType.LicenceNumber:
                const providerDto = dto as DirectoryHealthCareProviderSearchDuplicateDto;
                pathParams = [providerDto.licenseOrganizationRuleId, providerDto.licenseNumber];
                break;
            case DuplicateSearchType.Pharmacist:
                const pharmacist = dto as string;
                pathParams = [pharmacist];
                route = `${route}/pharmacists`;
                break;
        }

        return this.externalServiceClient.getAsync<SearchResponse>(route, pathParams, queryParams);
    }

    public getByIds(directoryType: DirectoryEntityType, ids: string[], legacyIds: string[] = null): Promise<DirectoryEntitySearchDto[]> {
        let queryParams: QueryParams = { ids: ids };

        if (legacyIds?.any())
            queryParams["legacyIds"] = legacyIds;

        return this.externalServiceClient.getAsync(this.routes[directoryType], null, queryParams);
    }

    public async getPharmacyNameByClientTag(clientTag: string): Promise<string> {
        const pharmacy = await this.externalServiceClient.getAsync<{ name: string }>(`${this.routes[DirectoryEntityType.Pharmacy]}/${clientTag}/address`);
        return pharmacy?.name;
    }

    public async getSelectableEntityById(directoryType: DirectoryEntityType, id: string): Promise<DirectorySelectableEntity<IDirectoryEntity> | null> {
        const client = ContactDirectoryClient.getInstance();

        const contactDirectoryResult = await client.getByIds(
            directoryType,
            [id]
        );

        if (!contactDirectoryResult?.any())
            return null;

        let entity;
        switch (directoryType) {
            case DirectoryEntityType.Other:
                entity = DirectoryOtherEstablishment.fromSearchDto(contactDirectoryResult[0] as DirectoryOtherEstablishmentSearchDto);
                break;
            case DirectoryEntityType.HealthcareProvider:
                entity = DirectoryHealthCareProvider.fromSearchDto(contactDirectoryResult[0] as DirectoryHealthCareProviderSearchDto);
                break;
            case DirectoryEntityType.Pharmacy:
                entity = DirectoryPharmacy.fromSearchDto(contactDirectoryResult[0] as PharmacySearchDto);
                break;
        }

        return DirectoryEntityMapper.toDirectorySelectableEntity(entity);
    }
}

export { ContactDirectoryClient, SearchResponse };
